import React, { forwardRef } from 'react';
import { Input } from '@/components/ui/input';
import { formatPhoneNumber, getPhoneConfig } from '@/utils/phoneFormats';

/**
 * InputMask - Composant Input avec masque de saisie
 * Supporte différents types de masques prédéfinis
 */
export const InputMask = forwardRef(({ 
  mask = 'none',
  value = '',
  onChange,
  className = '',
  country = 'Côte d\'Ivoire', // Country for phone formatting
  ...props 
}, ref) => {
  
  /**
   * Masques prédéfinis
   */
  const masks = {
    phone: {
      pattern: () => getPhoneConfig(country).format,
      format: (val) => formatPhoneNumber(val, country),
      maxLength: () => getPhoneConfig(country).format.length + 5, // Add buffer for spaces
      placeholder: () => getPhoneConfig(country).placeholder
    },
    
    immatriculation: {
      pattern: 'XX-XXX-9999-XX',
      format: (val) => {
        // Format très flexible - accepte alphanumériques, tirets, espaces et slashes
        // Permet CI-ABJ-1234-AA, BF123456, ML-789-CD, 1234 AB 567, AB/123, etc.
        const clean = val.toUpperCase().replace(/[^A-Z0-9\- /]/g, '');
        
        // Pas de formatage automatique - juste nettoyage et majuscules
        // Ceci permet tous les formats d'Afrique de l'Ouest et d'autres pays
        return clean;
      },
      maxLength: 20, // Longueur flexible pour différents formats de pays
      placeholder: 'Ex: CI-ABJ-1234-AA, BF123456, ML-789'
    },
    
    none: {
      pattern: '',
      format: (val) => val,
      maxLength: undefined,
      placeholder: ''
    }
  };
  
  const currentMask = masks[mask] || masks.none;
  
  // Get dynamic values for phone mask
  const getMaskValue = (key) => {
    if (mask === 'phone' && typeof currentMask[key] === 'function') {
      return currentMask[key]();
    }
    return currentMask[key];
  };
  
  /**
   * Handle change with mask formatting
   */
  const handleChange = (e) => {
    const rawValue = e.target.value;
    
    if (mask === 'none') {
      onChange(e);
      return;
    }
    
    // Apply mask
    const formatted = currentMask.format(rawValue);
    
    // Create synthetic event with formatted value
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: formatted
      }
    };
    
    onChange(syntheticEvent);
  };
  
  return (
    <Input
      ref={ref}
      value={value}
      onChange={handleChange}
      maxLength={getMaskValue('maxLength')}
      placeholder={props.placeholder || getMaskValue('placeholder')}
      className={className}
      {...props}
    />
  );
});

InputMask.displayName = 'InputMask';

export default InputMask;
